import React, { useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { RadioButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { RadioGroup } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/RadioButtons'
import { ChevronIcon, ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { Popover } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/popover/Popover'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { DBTTeensRelationshipFilter } from '../../data/ltClientsApi'

const FilterButton = styled(PressableOpacity)(({ theme }) => ({
  padding: `${theme.spacing['12px']} ${theme.spacing['16px']}`,
  border: `1px solid ${theme.colors.borderActive}`,
  borderRadius: '4px',
  alignSelf: 'flex-start',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing['4px'],
}))

type DBTTeensFilterDropdownProps = {
  selectedFilter: DBTTeensRelationshipFilter
  onFilterChange: (filter: DBTTeensRelationshipFilter) => void
}

const DBTTeensFilterDropdown: React.FC<DBTTeensFilterDropdownProps> = ({ selectedFilter, onFilterChange }) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()
  const filterRef = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  const filterOptions = [
    {
      id: 'all' as DBTTeensRelationshipFilter,
      text: formatMessage({
        defaultMessage: 'All clients',
        description: 'Filter option to show all DBT Teens clients',
      }),
    },
    {
      id: 'primary' as DBTTeensRelationshipFilter,
      text: formatMessage({
        defaultMessage: 'Primary clients',
        description: 'Filter option to show only primary DBT Teens clients',
      }),
    },
    {
      id: 'interacted_with' as DBTTeensRelationshipFilter,
      text: formatMessage({
        defaultMessage: 'Group, family, caregiver-training, or meds clients',
        description: 'Filter option to show DBT Teens clients where the provider is a secondary provider',
      }),
    },
  ]

  const selectedLabel = filterOptions.find((o) => o.id === selectedFilter)?.text ?? ''

  return (
    <>
      <Popover
        trigger={filterRef}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placement='bottom left'
        width='320px'
        title={formatMessage({
          defaultMessage: 'Filter by relationship',
          description: 'Title for DBT Teens client relationship filter popover',
        })}
        testIdPrefix='ClientListV3-dbtTeensRelationshipFilter'
      >
        <RadioGroup
          buttons={filterOptions.map((option) => ({
            label: option.text,
            value: option.id,
          }))}
          selectedValue={selectedFilter}
          onChange={(value) => {
            onFilterChange(value as DBTTeensRelationshipFilter)
            setIsOpen(false)
          }}
          name='dbtTeensRelationshipFilter'
          baseInputStyle={{ marginBottom: 0 }}
          buttonType={RadioButtonType.DESCRIPTION_NO_OUTLINE}
          buttonStyle={{ marginBottom: 8 }}
        />
      </Popover>
      <FilterButton
        pressableRef={filterRef}
        onPress={() => setIsOpen((prev) => !prev)}
        testID={tID('ClientListV3-dbtTeensRelationshipFilter-trigger')}
        accessibilityLabel={formatMessage({
          defaultMessage: 'Filter clients by relationship type',
          description: 'Accessibility label for DBT Teens client relationship filter button',
        })}
      >
        <BodyText text={selectedLabel} />
        <ChevronIcon
          direction={isOpen ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
          size={16}
          fillColor={colors.iconDefault}
        />
      </FilterButton>
    </>
  )
}

export default DBTTeensFilterDropdown
